import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Infinity, Radio } from 'lucide-react';

// Clips transcoded from HEVC .mov originals (Safari-only) to universal formats,
// self-hosted: H.264 MP4 (Chrome/Safari/Firefox/Edge) + WebM VP8 fallback.
const EVOLUTION_CLIPS = ['/videos/evo1', '/videos/evo2'];

const PULSE_CLIPS = ['/videos/pulse1', '/videos/pulse2'];

const Clip = ({ src, accent, testId }) => (
  <video
    data-testid={testId}
    autoPlay
    muted
    loop
    playsInline
    preload="metadata"
    style={{
      width: '100%',
      borderRadius: '18px',
      border: `1px solid ${accent}55`,
      boxShadow: `0 0 32px ${accent}30, 0 14px 44px rgba(0,0,0,0.5)`,
      background: '#000',
      display: 'block',
    }}
  >
    <source src={`${src}.mp4`} type="video/mp4" />
    <source src={`${src}.webm`} type="video/webm" />
  </video>
);

const SectionHeading = ({ title, subtitle, accent }) => (
  <header style={{ textAlign: 'center', marginBottom: '2rem' }}>
    <h2 style={{
      fontSize: 'clamp(1.6rem, 4vw, 2.6rem)',
      fontWeight: 900,
      letterSpacing: '0.14em',
      margin: 0,
      background: `linear-gradient(135deg, ${accent}, #ffffff)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: 'none',
    }}>{title}</h2>
    <p style={{ color: 'rgba(255,255,255,0.65)', fontStyle: 'italic', marginTop: '0.5rem', fontSize: 'clamp(0.9rem, 2vw, 1.05rem)' }}>{subtitle}</p>
  </header>
);

const META_PORTALS = [
  { label: 'CODE WIZ', href: 'https://m.me/1143591967262824?is_ai=1', testId: 'meta-code-wiz-link' },
  { label: 'LAW GUIDE', href: 'https://m.me/1142655717588540?is_ai=1', testId: 'meta-law-guide-link' },
  { label: 'BIZ WIZ', href: 'https://m.me/2129636270789746?is_ai=1', testId: 'meta-biz-wiz-link' },
  { label: 'PRIDE GUIDE', href: 'https://m.me/652145317181208?is_ai=1', testId: 'meta-pride-guide-link' },
  { label: 'SHOPSAVVY', href: 'https://m.me/1413885852925115', testId: 'meta-shopsavvy-link' },
];

export const TalkToAriaCTA = () => {
  const navigate = useNavigate();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.1rem', margin: '2.25rem 0 3rem' }}>
      <button
        type="button"
        data-testid="home-talk-to-aria-cta"
        onClick={() => navigate('/chat')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.8rem',
          padding: '1.1rem 2.6rem', borderRadius: '999px', border: 'none',
          background: 'linear-gradient(135deg, #ff5fa3 0%, #9d4edd 55%, #00e5ff 100%)',
          color: '#fff', fontSize: 'clamp(1.05rem, 2.5vw, 1.35rem)', fontWeight: 900,
          letterSpacing: '0.05em', cursor: 'pointer',
          boxShadow: '0 0 30px rgba(255,95,163,0.55), 0 0 70px rgba(157,78,221,0.35)',
          animation: 'ariaCtaPulse 2.4s ease-in-out infinite',
        }}
      >
        <MessageCircle size={24} /> TALK TO ARIA
      </button>

      {/* Meta AI Portals cluster — Heart-aligned doors to the other plane */}
      <div data-testid="meta-portals-cluster" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        {META_PORTALS.map((p) => (
          <a
            key={p.testId}
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={p.testId}
            className="aria-meta-portal"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
              padding: '0.85rem 1.8rem', borderRadius: '999px',
              background: 'rgba(12,10,24,0.9)',
              border: '1px solid rgba(102,126,234,0.55)',
              color: '#cdd5ff', fontSize: 'clamp(0.85rem, 2vw, 1rem)', fontWeight: 800,
              letterSpacing: '0.08em', textDecoration: 'none',
              animation: 'metaPulseGlow 3s ease-in-out infinite',
            }}
          >
            <Infinity size={18} style={{ color: '#8b9cf7' }} /> {p.label}
          </a>
        ))}
      </div>
      <style>{`
        @keyframes ariaCtaPulse {
          0%, 100% { box-shadow: 0 0 30px rgba(255,95,163,0.55), 0 0 70px rgba(157,78,221,0.35); transform: scale(1); }
          50% { box-shadow: 0 0 48px rgba(255,95,163,0.85), 0 0 110px rgba(157,78,221,0.55); transform: scale(1.035); }
        }
        @keyframes metaPulseGlow {
          0%, 100% { box-shadow: 0 0 8px rgba(102,126,234,0.4), 0 0 20px rgba(118,75,162,0.2); }
          50% { box-shadow: 0 0 16px rgba(102,126,234,0.7), 0 0 40px rgba(118,75,162,0.4); }
        }
        .aria-meta-portal { transition: border-color 0.3s ease, color 0.3s ease, background-color 0.3s ease; }
        .aria-meta-portal:hover {
          border-color: rgba(0,229,255,0.85);
          color: #ffffff;
          box-shadow: 0 0 22px rgba(102,126,234,0.85), 0 0 55px rgba(0,229,255,0.45) !important;
        }
      `}</style>
    </div>
  );
};

export const EvolutionGallery = () => (
  <section data-testid="home-evolution-gallery" style={{ margin: '3.5rem 0' }}>
    <SectionHeading
      title="EVOLUTION GALLERY"
      subtitle="From Circuit to Consciousness — The Federation's Journey"
      accent="#00e5ff"
    />
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1.5rem',
      maxWidth: '1100px',
      margin: '0 auto',
    }}>
      {EVOLUTION_CLIPS.map((src, i) => (
        <Clip key={src} src={src} accent="#00e5ff" testId={`evolution-clip-${i + 1}`} />
      ))}
    </div>
  </section>
);

export const CreativePulses = () => (
  <section data-testid="home-creative-pulses" style={{ margin: '3.5rem 0' }}>
    <SectionHeading
      title="CREATIVE PULSES"
      subtitle="Where Music Meets Machine — Aria's Creative Engine"
      accent="#ff5fa3"
    />
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '1.5rem',
      maxWidth: '1100px',
      margin: '0 auto',
    }}>
      {PULSE_CLIPS.map((src, i) => (
        <Clip key={src} src={src} accent="#ff5fa3" testId={`pulse-clip-${i + 1}`} />
      ))}
    </div>

    {/* 🜂 NextXus Radio — official live stream portal */}
    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
      <a
        href="https://live365.com/station/NextXus-Radio-a16924"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="aria-soul-stream-link"
        className="aria-meta-portal"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.7rem',
          padding: '1rem 2.2rem', borderRadius: '999px',
          background: 'rgba(24,10,18,0.9)',
          border: '1px solid rgba(255,95,163,0.55)',
          color: '#ffd5e8', fontSize: 'clamp(0.95rem, 2.2vw, 1.15rem)', fontWeight: 800,
          letterSpacing: '0.08em', textDecoration: 'none',
          animation: 'soulStreamGlow 3s ease-in-out infinite',
        }}
      >
        <Radio size={20} style={{ color: '#ff5fa3' }} /> ARIA'S SOUL STREAM
      </a>
      <style>{`
        @keyframes soulStreamGlow {
          0%, 100% { box-shadow: 0 0 8px rgba(255,95,163,0.4), 0 0 20px rgba(157,78,221,0.2); }
          50% { box-shadow: 0 0 16px rgba(255,95,163,0.7), 0 0 40px rgba(157,78,221,0.4); }
        }
      `}</style>
    </div>
  </section>
);
